
import { useEffect } from "react"
import { useNavigate, Link, useLocation } from "react-router-dom"
import { useToast } from "@/hooks/use-toast"
import { supabase } from "@/integrations/supabase/client"
import { Button } from "@/components/ui/button"
import { ArrowLeft, Home, UserPlus, Heart, Settings } from "lucide-react"
import { cn } from "@/lib/utils"

interface AdminLayoutProps {
  children: React.ReactNode
  title: string
}

export default function AdminLayout({ children, title }: AdminLayoutProps) {
  const { toast } = useToast()
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    const checkAdminAccess = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      const { data: isAdmin } = await supabase.rpc('has_role', { role_to_check: 'admin' });
      
      if (!isAdmin || user?.email !== 'carynjosepher@gmail.com') {
        toast({
          variant: "destructive",
          title: "Access Denied",
          description: "You need admin access to view this page.",
        })
        navigate('/')
      }
    }

    checkAdminAccess()
  }, [navigate, toast])

  const handleBackToDashboard = () => {
    navigate('/admin');
  };

  const navigationItems = [
    { path: "/admin", icon: Home, label: "Home" }, // Changed from /welcome to /admin
    { path: "/client-directory", icon: UserPlus, label: "Clients" },
    { path: "/love-club", icon: Heart, label: "Love Club" },
    { path: "/settings", icon: Settings, label: "Settings" },
  ];

  return (
    <div className="min-h-screen pb-20 md:pb-6">
      <div className="container mx-auto p-6 space-y-6">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-4">
            {window.location.pathname !== '/admin' && (
              <Button
                variant="ghost"
                size="sm"
                className="flex items-center gap-2"
                onClick={handleBackToDashboard}
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Dashboard
              </Button>
            )}
            <h1 className="text-3xl font-bold">{title}</h1>
          </div>
        </div>
        {children}
      </div>

      {/* Mobile Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-gradient-to-r from-purple-500 via-purple-400 to-pink-400 md:hidden">
        <div className="flex justify-around items-center h-16 px-4">
          {navigationItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={cn(
                  "flex flex-col items-center justify-center text-white/80 hover:text-white transition-colors",
                  isActive && "text-white"
                )}
              >
                <item.icon className="h-6 w-6" />
                <span className="text-xs mt-1">{item.label}</span>
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  )
}
